import { Link } from "react-router-dom";
import {
  ShieldCheck,
  Package,
  Sparkles,
  Boxes,
  Tag,
  Truck,
  Clock,
  MapPin,
  Layers,
  PackagePlus,
  Scissors,
  Warehouse,
  AlertTriangle,
  Shield,
  ArrowRight,
} from "lucide-react";
import BigDemenageurCTA from "@/components/BigDemenageurCTA";

const Sections = ({ city }) => (
  <>
    {/* ======= SECTION 2 — Livraison à domicile ======= */}
    <section className="py-14 md:py-20 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          <div className="lg:col-span-7">
            <div className="overline" style={{ color: "#237cb5" }}>
              Livraison à domicile
            </div>
            <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
              Votre livraison de cartons directement à domicile
              {" "}{city.aCity}
            </h2>
            <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
              Parce que charger des paquets de cartons volumineux dans
              votre véhicule personnel au milieu des allées
              résidentielles du Vésinet est une contrainte évitable,
              notre service prend tout en charge.
            </p>
          </div>

          <div className="lg:col-span-5 space-y-4">
            {[
              {
                title: "Logistique de proximité complète",
                body: "Nous acheminons l'intégralité de vos commandes à plat directement sur le pas de votre porte au Vésinet, ainsi que dans les communes voisines comme Chatou, Croissy-sur-Seine, Saint-Germain-en-Laye et Montesson.",
                Icon: MapPin,
              },
              {
                title: "Respect strict de votre planning",
                body: "Pas de journée bloquée à attendre le livreur. Nous fixons ensemble un créneau horaire précis selon vos disponibilités. Vos fournitures pro vous sont remises dans un état de propreté impeccable, 100 % sèches et prêtes à être pliées instantanément.",
                Icon: Clock,
              },
            ].map(({ title, body, Icon }) => (
              <div
                key={title}
                className="flex items-start gap-4 bg-white border border-[#cfdbe6] rounded-2xl p-5 md:p-6"
              >
                <span className="flex items-center justify-center shrink-0 h-11 w-11 rounded-full bg-[#237cb5] text-white">
                  <Icon className="h-5 w-5" strokeWidth={2.4} />
                </span>
                <div>
                  <h3 className="font-display font-extrabold text-[15.5px] text-[#0f1d2b] leading-tight">
                    {title}
                  </h3>
                  <p className="mt-2 text-[14px] text-[#3a4856] leading-relaxed">
                    {body}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>

    {/* ======= SECTION 3 — Fournitures haute performance ======= */}
    <section className="py-14 md:py-20 bg-white">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Haute performance
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Nos fournitures d&apos;emballage et de protection haute
            performance
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Pour faire vos cartons comme un professionnel, notre
            boutique en ligne regroupe tout le matériel nécessaire pour
            sécuriser vos biens.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
          {[
            {
              title: "La cartonnerie technique",
              body: "Cartons standards polyvalents, bacs petits formats double cannelure pour les livres lourds, cartons penderies verticaux pour suspendre vos vêtements sur cintres, et boîtes à croisillons pour isoler vos verres et assiettes fines.",
              Icon: Boxes,
            },
            {
              title: "Les barrières anti-chocs",
              body: "Rouleaux de film à bulles haute densité pour envelopper le fragile, papier kraft de calage pour combler les espaces vides, et profilés en mousse pour protéger les angles de vos tableaux.",
              Icon: PackagePlus,
            },
            {
              title: "Les accessoires de scellage",
              body: "Rouleaux d'adhésif industriel silencieux, dévidoirs pistolets ergonomiques pour fermer vos colis en un geste, cutters sécurisés et marqueurs indélébiles pour identifier vos pièces.",
              Icon: Scissors,
            },
          ].map(({ title, body, Icon }) => (
            <div
              key={title}
              className="flex items-start gap-4 bg-[#EEF3F7] border border-[#cfdbe6] rounded-2xl p-5 md:p-6"
            >
              <span className="flex items-center justify-center shrink-0 h-11 w-11 rounded-full bg-[#237cb5] text-white">
                <Icon className="h-5 w-5" strokeWidth={2.4} />
              </span>
              <div>
                <h3 className="font-display font-extrabold text-[15.5px] text-[#0f1d2b] leading-tight">
                  {title}
                </h3>
                <p className="mt-2 text-[14px] text-[#3a4856] leading-relaxed">
                  {body}
                </p>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-8 flex flex-wrap gap-3">
          <Link
            to="/cartons-demenagement"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
            data-testid="city-le-vesinet-cta-shop"
          >
            <Boxes className="h-4 w-4" strokeWidth={2.6} />
            Voir toute la gamme
            <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
          </Link>
          <Link
            to="/fermeture-accessoires"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
            data-testid="city-le-vesinet-cta-accessoires"
          >
            <Tag className="h-4 w-4" strokeWidth={2.6} />
            Adhésifs &amp; protections
          </Link>
        </div>
      </div>
    </section>

    {/* ======= CTA Big Déménagement (au milieu) ======= */}
    <section className="py-12 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <BigDemenageurCTA testId={`city-${city.slug}-bigdem-cta-middle`} />
      </div>
    </section>

    {/* ======= SECTION 4 — Garde-meuble : conseils experts ======= */}
    <section className="py-14 md:py-20 bg-white">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Conseils garde-meuble
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Comment bien protéger vos meubles destinés au garde-meuble
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Le stockage à long terme en box de stockage ou en
            garde-meuble impose des règles strictes pour lutter contre
            l&apos;humidité résiduelle, la poussière et la compression.
            Voici les bons réflexes de nos experts.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-5">
          {/* Pas de film étirable direct */}
          <div className="bg-[#EEF3F7] border border-[#cfdbe6] rounded-2xl p-6 md:p-7">
            <div className="flex items-center gap-3">
              <span className="flex items-center justify-center h-11 w-11 rounded-full bg-[#237cb5] text-white">
                <AlertTriangle className="h-5 w-5" strokeWidth={2.4} />
              </span>
              <h3 className="font-display font-extrabold text-[18px] text-[#0f1d2b] leading-tight">
                Le bannissement du film étirable direct sur le bois
              </h3>
            </div>
            <p className="mt-4 text-[14.5px] text-[#3a4856] leading-relaxed">
              Ne filmez jamais un meuble en bois directement avec du
              plastique étirable. Sous l&apos;effet des variations de
              température, le plastique crée de la condensation qui
              fait moisir le bois. Utilisez toujours une couverture de
              déménagement ou du Bullkraft (bulle + papier kraft
              absorbant) en première couche.
            </p>
            <div className="mt-5 space-y-2 text-[13px] text-[#3a4856]">
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                Pas de plastique direct sur le bois
              </div>
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                Bullkraft : bulle + kraft absorbant
              </div>
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                Couvertures de déménagement
              </div>
            </div>
          </div>

          {/* Armure totale literie */}
          <div className="bg-[#EEF3F7] border border-[#cfdbe6] rounded-2xl p-6 md:p-7">
            <div className="flex items-center gap-3">
              <span className="flex items-center justify-center h-11 w-11 rounded-full bg-[#237cb5] text-white">
                <Shield className="h-5 w-5" strokeWidth={2.4} />
              </span>
              <h3 className="font-display font-extrabold text-[18px] text-[#0f1d2b] leading-tight">
                L&apos;armure totale pour la literie
              </h3>
            </div>
            <p className="mt-4 text-[14.5px] text-[#3a4856] leading-relaxed">
              Les matelas et sommiers sont les premières victimes de la
              poussière en garde-meuble. Glissez-les impérativement dans
              nos housses de matelas étanches en polyéthylène épais et
              scotchez l&apos;ouverture hermétiquement pour bloquer les
              acariens et l&apos;humidité.
            </p>
            <div className="mt-5 space-y-2 text-[13px] text-[#3a4856]">
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                Housses polyéthylène épais
              </div>
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                Scellage hermétique
              </div>
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                Anti-acariens, anti-humidité
              </div>
            </div>
          </div>
        </div>

        <div className="mt-8 flex flex-wrap gap-3">
          <Link
            to="/fermeture-accessoires"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
            data-testid="city-le-vesinet-cta-housses"
          >
            <Warehouse className="h-4 w-4" strokeWidth={2.6} />
            Voir les housses &amp; protections
            <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
          </Link>
          <Link
            to="/livraison"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
            data-testid="city-le-vesinet-cta-livraison"
          >
            <Truck className="h-4 w-4" strokeWidth={2.6} />
            Nos modalités de livraison
          </Link>
        </div>
      </div>
    </section>

    {/* ======= SECTION 5 — CTA retour département ======= */}
    <section className="py-10 md:py-14 bg-[#EEF3F7] border-t border-[#cfdbe6]">
      <div className="container-cdm">
        <Link
          to={`/cartons-demenagement/${city.deptSlug}`}
          className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
          data-testid="city-le-vesinet-cta-back-dept"
        >
          <Package className="h-4 w-4" strokeWidth={2.6} />
          Voir la page {city.deptName} ({city.deptCode})
        </Link>
      </div>
    </section>
  </>
);

export default { Sections };
